import React, { useEffect, useMemo, useState } from "react";
import {
  FaArrowRight,
  FaCalendarAlt,
  FaCheckCircle,
  FaCity,
  FaEnvelope,
  FaEuroSign,
  FaFileAlt,
  FaHandsHelping,
  FaImage,
  FaMapMarkedAlt,
  FaMousePointer,
  FaNewspaper,
  FaPhoneAlt,
  FaRegPlayCircle,
  FaRocket,
  FaStore,
  FaUsers,
  FaUsersCog,
} from "react-icons/fa";

import useScrollToTop from "../../hooks/useScollToTop";
import ScrollIndicator from "../Home/ScrollIndicator";
import Reveal from "../../components/ui/Reveal";

const LOGO_MONAPPLIMairie =
  "https://api.monapplimairie.com/uploads/media/images/2026/06/logo-monapplimairie_1780748151121_jgku4h.webp";

  const VILLAGE_DEMO_IMAGE =
  "https://api.monapplimairie.com/uploads/media/images/2026/06/chatgpt-image-6-juin-2026-19-13-58_1780766099087_mc526b.webp";

const CONTACT_PHONE = "0680727707";
const CONTACT_PHONE_LABEL = "06 80 72 77 07";
const CONTACT_EMAIL = "contact@monapplimairie.com";

const features = [
  {
    icon: FaNewspaper,
    title: "Actualités",
    text: "Publiez facilement les informations importantes de votre commune.",
  },
  {
    icon: FaCalendarAlt,
    title: "Agenda",
    text: "Affichez les événements, réunions, animations et cérémonies à venir.",
  },
  {
    icon: FaUsers,
    title: "Associations",
    text: "Mettez en valeur les associations locales et leurs coordonnées.",
  },
  {
    icon: FaStore,
    title: "Commerces",
    text: "Présentez les commerces, artisans et services de proximité.",
  },
  {
    icon: FaUsersCog,
    title: "Élus et services",
    text: "Présentez les élus, les services municipaux, les horaires et contacts.",
  },
  {
    icon: FaFileAlt,
    title: "Documents",
    text: "Mettez à disposition bulletins, comptes rendus et documents publics.",
  },
  {
    icon: FaMapMarkedAlt,
    title: "Carte interactive",
    text: "Aidez les habitants à localiser les lieux utiles de la commune.",
  },
  {
    icon: FaCity,
    title: "Pages communales",
    text: "Ajoutez des pages sur la commune : histoire, tourisme, enfance, seniors…",
  },
];

const reassuranceItems = [
  "Vous gardez la main sur les informations de votre commune.",
  "Aucune compétence informatique particulière n’est nécessaire.",
  "Le site fonctionne sur ordinateur, tablette et téléphone.",
  "Je vous accompagne pour la mise en place et la prise en main.",
  "Vous pouvez demander de l’aide à tout moment.",
  "L’application évolue et les mises à jour sont incluses.",
];

const plans = [
  {
    id: "small",
    label: "Communes de moins de 1 000 habitants",
    price: 240,
    hosting: 60,
    badge: "Petites communes",
    description:
      "Pour une commune qui souhaite un site clair, moderne et facile à mettre à jour.",
  },
  {
    id: "medium",
    label: "Communes de 1 000 à 5 000 habitants",
    price: 340,
    hosting: 60,
    badge: "Communes intermédiaires",
    description:
      "Pour une mairie avec plus d’informations à publier et plusieurs services à présenter.",
    highlighted: true,
  },
  {
    id: "large",
    label: "Communes de plus de 5 000 habitants",
    price: 440,
    hosting: 60,
    badge: "Communes plus importantes",
    description:
      "Pour une commune qui veut une solution complète, accompagnée et durable.",
  },
];

const previewSlides = [
  {
    src: "/img/diapo/Diapo_001.webp",
    title: "Une page d’accueil claire",
    tag: "Côté Visiteurs",
    legend:
      "La commune dispose d’une page d’accueil moderne avec photos, raccourcis, actualités et informations importantes.",
  },
  {
    src: "/img/diapo/Diapo_002.webp",
    title: "Présentation de la commune et messages importants",
    tag: "Côté Visiteurs",
    legend:
      "Une présentation de la commune permettant un bon référencement. Les messages importants peuvent être mis en avant pour être visibles dès l’arrivée sur le site.",
  },
  {
    src: "/img/diapo/Diapo_003.webp",
    title: "Formulaire de contact",
    tag: "Côté Visiteurs",
    legend:
      "Les habitants peuvent contacter la mairie facilement via un formulaire en ligne et demander à recevoir les informations ou Newsletters par mail.",
  },
  {
    src: "/img/diapo/Diapo_004.webp",
    title: "Menu de navigation",
    tag: "Côté Visiteurs",
    legend:
      "Un menu de navigation clair et accessible depuis n’importe quelle page du site pour trouver rapidement les informations recherchées. Compatibilité avec tous les types d’écrans (ordinateur, tablette, téléphone).",
  },
  {
    src: "/img/diapo/Diapo_005.webp",
    title: "Des informations mises en avant sur la page Accueil",
    tag: "Côté Visiteurs",
    legend:
      "Les dernières actualites, les événements à venir, les projets ainsi qu'une carte intéractive sont mis en avant pour être visibles dès l’arrivée sur le site.",
  },
  {
    src: "/img/diapo/Diapo_006.webp",
    title: "Présentation des élus et conseil municipal",
    tag: "Côté Visiteurs",
    legend:
      "Une page dédiée pour présenter les élus, le conseil municipal, les commissions et les services municipaux avec leurs coordonnées et horaires d’ouverture pour faciliter les démarches des habitants.",
  },
  {
    src: "/img/diapo/Diapo_007.webp",
    title: "Pages internes",
    tag: "Côté Visiteurs",
    legend:
      "Retrouvez facilement les informations importantes de la commune : les élus, les services municipaux, les associations, les commerces, les documents importants, etc.",
  },
  {
    src: "/img/diapo/Diapo_008.webp",
    title: "Mise en valeur des artisans et commerces locaux",
    tag: "Côté Visiteurs",
    legend:
      "Une page dédiée pour présenter les commerces, artisans et services de proximité de la commune avec leurs coordonnées et horaires d’ouverture.",
  },
  {
    src: "/img/diapo/Diapo_009.webp",
    title: "Carte interactive",
    tag: "Côté Visiteurs",
    legend:
      "Une carte interactive permettant de localiser les commerces, associations, services municipaux et autres lieux utiles de la commune pour les habitants et visiteurs.",
  },
  {
    src: "/img/diapo/Diapo_010.webp",
    title: "Page Personnalisée",
    tag: "Côté Visiteurs",
    legend:
      "La Mairie peut ajouter des pages personnalisées pour présenter la commune, son histoire, le tourisme, les projets en cours, les démarches administratives, etc. Les possibilités sont nombreuses pour donner aux habitants un site vivant et utile au quotidien.",
  },
  {
    src: "/img/diapo/Diapo_011.webp",
    title: "Tableau de bord de gestion",
    tag: "Côté Mairie",
    legend:
      "La Mairie peut gérer facilement les contenus du site, suivre les statistiques de fréquentation, et administrer les différentes fonctionnalités pour offrir un service optimal aux habitants.",
  },
  {
    src: "/img/diapo/Diapo_012.webp",
    title: "Gestion des informations facilitée",
    tag: "Côté Mairie",
    legend:
      "L’interface de gestion permet de publier une actualité, ajouter un événement ou modifier une page sans toucher au code du site. La prise en main est rapide et l’accompagnement est assuré pour vous aider à chaque étape.",
  },
  {
    src: "/img/diapo/Diapo_013.webp",
    title: "Création de Contenus simplifiée",
    tag: "Côté Mairie",
    legend:
      "L’outil de gestion est conçu pour être simple et intuitif, même pour les utilisateurs les moins à l’aise avec internet. Vous pouvez créer et organiser vos contenus facilement pour que votre site soit toujours à jour et pertinent pour les habitants.",
  },
];

function scrollToSection(id) {
  const target = document.getElementById(id);
  if (!target) return;

  target.scrollIntoView({
    behavior: "smooth",
    block: "start",
  });
}

function SectionTitle({
  eyebrow,
  title,
  description,
  centered = false,
  dark = false,
}) {
  return (
    <Reveal>
      <div className={centered ? "mx-auto max-w-3xl text-center" : "max-w-3xl"}>
        <div
          className={[
            "mb-5 inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-bold shadow-sm",
            dark
              ? "border-white/15 bg-white/10 text-white"
              : "border-slate-200 bg-white text-slate-700 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200",
          ].join(" ")}
        >
          <span className="h-2.5 w-2.5 rounded-full bg-[#1277B9]" />
          {eyebrow}
        </div>

        <h2
          className={[
            "text-3xl font-bold tracking-tight md:text-4xl lg:text-5xl",
            dark ? "text-white" : "text-slate-950 dark:text-white",
          ].join(" ")}
        >
          {title}
        </h2>

        {description && (
          <p
            className={[
              "mt-4 text-base leading-8 md:text-lg",
              dark ? "text-slate-200" : "text-slate-600 dark:text-slate-300",
            ].join(" ")}
          >
            {description}
          </p>
        )}
      </div>
    </Reveal>
  );
}

function HeroSection() {
  return (
    <section className="relative overflow-hidden bg-slate-950 px-4 py-16 text-white sm:px-6 lg:px-8 lg:py-20">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-24 top-16 h-80 w-80 rounded-full bg-sky-500/20 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-96 w-96 rounded-full bg-emerald-400/15 blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(14,165,233,0.28),transparent_36%),radial-gradient(circle_at_bottom_right,rgba(16,185,129,0.16),transparent_34%)]" />
      </div>

      <div className="relative mx-auto grid max-w-[1380px] gap-10 lg:grid-cols-[1.02fr_0.98fr] lg:items-center">
        <Reveal>
          <div>
            <img
              src={LOGO_MONAPPLIMairie}
              alt="Logo MonAppliMairie"
              className="mb-7 h-24 w-auto object-contain sm:h-28"
              loading="eager"
            />

            <div className="inline-flex items-center gap-3 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm font-bold text-sky-100 shadow-xl shadow-black/10 backdrop-blur">
              <FaRocket className="h-4 w-4" />
              Une solution simple pour les mairies
            </div>

            <h1 className="mt-7 max-w-5xl text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
              Un site internet moderne pour votre commune, facile à mettre à
              jour.
            </h1>

            <p className="mt-6 max-w-3xl text-lg leading-8 text-slate-200 sm:text-xl">
              MonAppliMairie permet aux petites et moyennes communes de publier
              leurs informations sans dépendre d’un prestataire à chaque
              modification.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() => scrollToSection("tarifs")}
                className="group inline-flex min-h-12 items-center justify-center gap-3 rounded-2xl bg-white px-6 py-3 text-base font-bold text-slate-950 shadow-lg shadow-black/20 transition hover:-translate-y-0.5 hover:bg-slate-100"
              >
                Voir les tarifs
                <FaArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
              </button>

              <button
                type="button"
                onClick={() => scrollToSection("contact-demo")}
                className="group inline-flex min-h-12 items-center justify-center gap-3 rounded-2xl border border-white/20 bg-white/10 px-6 py-3 text-base font-bold text-white shadow-lg shadow-black/10 backdrop-blur transition hover:-translate-y-0.5 hover:bg-white/15"
              >
                Demander une démonstration
                <FaRegPlayCircle className="h-4 w-4" />
              </button>
            </div>
          </div>
        </Reveal>

        <Reveal>
          <div className="relative">
            <div className="absolute -inset-4 rounded-[2.5rem] bg-sky-400/20 blur-3xl" />

            <div className="relative overflow-hidden rounded-[2.2rem] border border-white/10 bg-white/10 p-4 shadow-2xl shadow-black/30 backdrop-blur">
              <div className="rounded-[1.7rem] bg-white p-5 text-slate-950 dark:bg-slate-900 dark:text-white">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="rounded-3xl bg-sky-50 p-5 dark:bg-sky-500/10">
                    <FaMousePointer className="h-7 w-7 text-sky-600 dark:text-sky-300" />
                    <p className="mt-4 text-2xl font-bold">
                      Simple à utiliser
                    </p>
                    <p className="mt-1 text-sm font-bold text-slate-600 dark:text-slate-300">
                      même sans connaissance informatique
                    </p>
                  </div>

                  <div className="rounded-3xl bg-emerald-50 p-5 dark:bg-emerald-500/10">
                    <FaHandsHelping className="h-7 w-7 text-emerald-600 dark:text-emerald-300" />
                    <p className="mt-4 text-2xl font-bold">
                      Accompagnement
                    </p>
                    <p className="mt-1 text-sm font-bold text-slate-600 dark:text-slate-300">
                      installation, formation et assistance
                    </p>
                  </div>
                </div>

                <div className="mt-4 rounded-3xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-700 dark:bg-slate-800">
                  <p className="text-sm font-bold uppercase tracking-[0.18em] text-sky-600 dark:text-sky-300">
                    Pour qui ?
                  </p>

                  <p className="mt-2 text-xl font-bold">
                    Les communes qui veulent un site propre, vivant, rassurant
                    et facile à faire évoluer.
                  </p>
                </div>

                <div className="mt-4 rounded-3xl border border-emerald-200 bg-emerald-50 p-5 dark:border-emerald-500/20 dark:bg-emerald-500/10">
                  <p className="text-sm font-bold leading-7 text-emerald-800 dark:text-emerald-200">
                    Vous pouvez publier une actualité, ajouter un événement ou
                    modifier une page sans toucher au code du site.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function FullImageSection() {
  return (
    <section className="relative h-[72vh] min-h-[520px] w-full overflow-hidden bg-slate-950">
      <img
        src="/img/illustration_monapplimairie.webp"
        alt="Illustration MonAppliMairie pour les communes"
        className="h-full w-full object-cover"
        loading="lazy"
      />

      <div className="absolute inset-0 bg-gradient-to-r from-slate-950/85 via-slate-950/55 to-slate-950/20" />
      <div className="absolute inset-x-0 bottom-0 h-56 bg-gradient-to-t from-slate-950 to-transparent" />

      <div className="absolute inset-0 flex items-center">
        <div className="mx-auto w-full max-w-[1380px] px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="max-w-2xl text-white">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm font-bold backdrop-blur">
                <FaCity className="h-4 w-4" />
                Pensé pour les communes
              </div>

              <h2 className="mt-6 text-4xl font-bold tracking-tight md:text-5xl">
                Une mairie peut communiquer simplement, rapidement et proprement.
              </h2>

              <p className="mt-5 text-lg leading-8 text-slate-200">
                L’objectif est de vous faire gagner du temps tout en donnant aux
                habitants un site clair, utile et agréable à consulter.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function ReassuranceSection() {
  return (
    <section className="relative overflow-hidden bg-slate-50 px-4 py-14 dark:bg-slate-950 sm:px-6 lg:px-8 lg:py-20">
      <div className="relative mx-auto max-w-[1380px]">
        <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
          <SectionTitle
            eyebrow="Pourquoi choisir MonAppliMairie ?"
            title="Une solution rassurante pour les petites communes."
            description="Vous disposez d’un site moderne, d’un outil de gestion simple et d’un accompagnement humain pour vous aider à chaque étape."
          />

          <Reveal>
            <div className="grid gap-3 sm:grid-cols-2">
              {reassuranceItems.map((item) => (
                <div
                  key={item}
                  className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900"
                >
                  <FaCheckCircle className="mt-1 h-4 w-4 shrink-0 text-emerald-500" />
                  <p className="text-sm font-bold leading-6 text-slate-700 dark:text-slate-200">
                    {item}
                  </p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function FeaturesSection() {
  return (
    <section
      id="fonctionnalites"
      className="relative overflow-hidden bg-slate-50 px-4 py-14 dark:bg-slate-950 sm:px-6 lg:px-8 lg:py-20"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-24 top-16 h-72 w-72 rounded-full bg-sky-300/15 blur-3xl dark:bg-sky-500/10" />
        <div className="absolute -right-24 bottom-12 h-80 w-80 rounded-full bg-emerald-300/10 blur-3xl dark:bg-emerald-500/10" />
      </div>

      <div className="relative mx-auto max-w-[1380px]">
        <SectionTitle
          eyebrow="Ce que vous pouvez gérer"
          title="Les informations essentielles de la commune au même endroit."
          description="MonAppliMairie regroupe les contenus les plus utiles pour les habitants, visiteurs, associations et services municipaux."
          centered
        />

        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {features.map((feature, index) => {
            const Icon = feature.icon;

            return (
              <Reveal key={feature.title} delay={index * 70} className="h-full">
                <article className="group flex h-full flex-col rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm transition duration-300 hover:-translate-y-1 hover:border-[#1277B9]/50 hover:shadow-2xl hover:shadow-slate-950/10 dark:border-slate-800 dark:bg-slate-900">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#1277B9]/10 text-[#1277B9] transition group-hover:bg-[#1277B9] group-hover:text-white">
                    <Icon className="h-5 w-5" />
                  </div>

                  <h3 className="mt-5 text-xl font-bold text-slate-950 dark:text-white">
                    {feature.title}
                  </h3>

                  <p className="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-300">
                    {feature.text}
                  </p>
                </article>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function PreviewSlider() {
  const [index, setIndex] = useState(0);
  const [failedImages, setFailedImages] = useState({});

  const current = previewSlides[index];

  const canShowImage = useMemo(
    () => current?.src && !failedImages[current.src],
    [current, failedImages],
  );

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((currentIndex) => (currentIndex + 1) % previewSlides.length);
    }, 6000);

    return () => window.clearInterval(timer);
  }, []);

  const prev = () => {
    setIndex((currentIndex) =>
      currentIndex === 0 ? previewSlides.length - 1 : currentIndex - 1,
    );
  };

  const next = () => {
    setIndex((currentIndex) => (currentIndex + 1) % previewSlides.length);
  };

  return (
    <section
      id="apercu"
      className="relative overflow-hidden bg-slate-50 px-4 py-14 dark:bg-slate-950 sm:px-6 lg:px-8 lg:py-20"
    >
      <div className="relative mx-auto max-w-[1380px]">
        <div className="mb-10 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <SectionTitle
            eyebrow="Aperçu en images"
            title="Voyez rapidement à quoi ressemble le site et son espace de gestion."
            description="Un seul aperçu pour comprendre le rendu côté habitants et la simplicité d’utilisation côté mairie."
          />

          <Reveal>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={prev}
                className="inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:text-[#1277B9] dark:border-slate-800 dark:bg-slate-900 dark:text-white"
                aria-label="Capture précédente"
              >
                <FaArrowRight className="h-4 w-4 rotate-180" />
              </button>

              <button
                type="button"
                onClick={next}
                className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#1277B9] text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-[#0f659d]"
                aria-label="Capture suivante"
              >
                <FaArrowRight className="h-4 w-4" />
              </button>
            </div>
          </Reveal>
        </div>

        <Reveal>
          <div className="overflow-hidden rounded-[2.2rem] border border-slate-200 bg-white shadow-2xl shadow-slate-950/10 dark:border-slate-800 dark:bg-slate-900">
            <div className="relative bg-slate-950">
              <div className="aspect-[16/9] w-full bg-slate-950">
                {canShowImage ? (
                  <img
                    src={current.src}
                    alt={current.title}
                    className="h-full w-full object-contain"
                    loading="lazy"
                    onError={() =>
                      setFailedImages((currentFailedImages) => ({
                        ...currentFailedImages,
                        [current.src]: true,
                      }))
                    }
                  />
                ) : (
                  <div className="flex h-full min-h-[340px] flex-col items-center justify-center bg-slate-100 p-8 text-center dark:bg-slate-800">
                    <div className="flex h-20 w-20 items-center justify-center rounded-3xl bg-white text-slate-400 shadow-sm dark:bg-slate-900">
                      <FaImage className="h-9 w-9" />
                    </div>

                    <p className="mt-6 text-xl font-bold text-slate-900 dark:text-white">
                      Capture à ajouter
                    </p>

                    <p className="mt-2 max-w-md text-base leading-7 text-slate-600 dark:text-slate-300">
                      Image attendue :{" "}
                      <span className="font-bold">{current.src}</span>
                    </p>
                  </div>
                )}
              </div>

              <div className="border-t border-white/10 bg-slate-950 px-5 py-5 text-white md:px-7">
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                  <div>
                    <span className="inline-flex rounded-full bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-sky-200">
                      {current.tag}
                    </span>

                    <h3 className="mt-3 text-xl font-bold">{current.title}</h3>

                    <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-300">
                      {current.legend}
                    </p>
                  </div>

                  <p className="text-sm font-bold text-slate-400">
                    {index + 1} / {previewSlides.length}
                  </p>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  {previewSlides.map((slide, slideIndex) => (
                    <button
                      key={`${slide.title}-${slideIndex}`}
                      type="button"
                      onClick={() => setIndex(slideIndex)}
                      aria-label={`Afficher ${slide.title}`}
                      className={`h-2.5 rounded-full transition-all ${
                        slideIndex === index
                          ? "w-9 bg-white"
                          : "w-2.5 bg-white/30 hover:bg-white/60"
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function PricingSection() {
  return (
    <section
      id="tarifs"
      className="relative overflow-hidden bg-slate-50 px-4 py-14 dark:bg-slate-950 sm:px-6 lg:px-8 lg:py-20"
    >
      <div className="relative mx-auto max-w-[1380px]">
        <SectionTitle
          eyebrow="Tarifs"
          title="Des tarifs annuels simples, adaptés à la taille de votre commune."
          description="La solution reste la même pour toutes les communes. Seul le tarif change selon le nombre d’habitants."
          centered
        />

        <div className="mt-10 grid gap-5 lg:grid-cols-3">
          {plans.map((plan, index) => (
            <Reveal key={plan.id} delay={index * 90} className="h-full">
              <article
                className={`relative flex h-full flex-col overflow-hidden rounded-[2rem] border bg-white p-6 shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-2xl hover:shadow-slate-950/10 dark:bg-slate-900 ${
                  plan.highlighted
                    ? "border-[#1277B9] ring-4 ring-[#1277B9]/10"
                    : "border-slate-200 dark:border-slate-800"
                }`}
              >
                {plan.highlighted && (
                  <div className="absolute right-5 top-5 rounded-full bg-[#1277B9] px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-white">
                    Le plus courant
                  </div>
                )}

                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#1277B9]">
                  {plan.badge}
                </p>

                <h3 className="mt-3 pr-20 text-2xl font-bold text-slate-950 dark:text-white">
                  {plan.label}
                </h3>

                <p className="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-300">
                  {plan.description}
                </p>

                <div className="mt-6 rounded-3xl bg-slate-50 p-5 dark:bg-slate-950">
                  <p className="text-4xl font-bold text-slate-950 dark:text-white">
                    {plan.price} €
                    <span className="text-base font-bold text-slate-500">
                      {" "}
                      / an
                    </span>
                  </p>

                  <p className="mt-2 text-sm font-semibold text-slate-500 dark:text-slate-400">
                    Abonnement annuel MonAppliMairie
                  </p>

                  <p className="mt-3 text-sm font-bold text-slate-700 dark:text-slate-300">
                    + {plan.hosting} € / an
                  </p>

                  <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
                    Pour l’hébergement du site, du serveur, de la base de
                    données et du nom de domaine.
                  </p>
                </div>

                <ul className="mt-6 space-y-3 text-sm font-semibold text-slate-600 dark:text-slate-300">
                  <li className="flex gap-2">
                    <FaCheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                    Mise en place du site
                  </li>
                  <li className="flex gap-2">
                    <FaCheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                    Formation en visio
                  </li>
                  <li className="flex gap-2">
                    <FaCheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                    Assistance dans la durée
                  </li>
                </ul>

                <button
                  type="button"
                  onClick={() => scrollToSection("contact-demo")}
                  className="mt-auto inline-flex min-h-12 items-center justify-center gap-3 rounded-2xl bg-[#1277B9] px-5 py-3 text-sm font-bold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-[#0f659d]"
                >
                  Demander un devis
                  <FaArrowRight className="h-4 w-4" />
                </button>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="mt-6 rounded-[2rem] border border-slate-200 bg-white p-5 text-sm leading-7 text-slate-600 shadow-sm dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300">
            Les tarifs sont annuels. Une réduction de parrainage peut être
            proposée si une commune recommande MonAppliMairie à une autre mairie.
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function HumanDemoSection() {
  return (
    <section className="relative overflow-hidden bg-slate-50 px-4 py-14 dark:bg-slate-950 sm:px-6 lg:px-8 lg:py-20">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-24 top-16 h-72 w-72 rounded-full bg-sky-300/15 blur-3xl dark:bg-sky-500/10" />
        <div className="absolute -right-24 bottom-12 h-80 w-80 rounded-full bg-emerald-300/10 blur-3xl dark:bg-emerald-500/10" />
      </div>

      <div className="relative mx-auto max-w-[1380px]">
        <div className="grid gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:items-center">
          <Reveal>
            <div>
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200">
                <span className="h-2.5 w-2.5 rounded-full bg-[#1277B9]" />
                Proche des habitants
              </div>

              <h2 className="text-3xl font-bold tracking-tight text-slate-950 dark:text-white md:text-4xl lg:text-5xl">
                Un site utile au quotidien, même pour les habitants les moins à
                l’aise avec internet.
              </h2>

              <p className="mt-5 max-w-3xl text-base leading-8 text-slate-600 dark:text-slate-300 md:text-lg">
                MonAppliMairie permet à la commune de mieux informer les
                habitants : actualités, événements, services, démarches,
                associations, commerces et documents importants sont accessibles
                simplement depuis un ordinateur, une tablette ou un téléphone.
              </p>

              <div className="mt-7 grid gap-3 sm:grid-cols-2">
                <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                  <div className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[#1277B9]/10 text-[#1277B9] dark:bg-sky-500/15 dark:text-sky-300">
                      <FaUsers className="h-4 w-4" />
                    </span>

                    <div>
                      <h3 className="text-sm font-bold text-slate-950 dark:text-white">
                        Plus de proximité
                      </h3>
                      <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">
                        La mairie communique plus facilement avec les habitants.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                  <div className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-emerald-500/10 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300">
                      <FaMousePointer className="h-4 w-4" />
                    </span>

                    <div>
                      <h3 className="text-sm font-bold text-slate-950 dark:text-white">
                        Plus simple à consulter
                      </h3>
                      <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">
                        Les informations importantes restent accessibles à tout moment.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={() => scrollToSection("contact-demo")}
                className="group mt-8 inline-flex min-h-12 items-center justify-center gap-3 rounded-2xl bg-[#1277B9] px-6 py-3 text-sm font-bold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-[#0f659d]"
              >
                Prévoir une démonstration
                <FaArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
              </button>
            </div>
          </Reveal>

          <Reveal>
            <div className="relative">
              <div className="absolute -inset-4 rounded-[2.5rem] bg-[#1277B9]/15 blur-3xl" />

              <figure className="relative overflow-hidden rounded-[2.3rem] border border-slate-200 bg-white shadow-2xl shadow-slate-950/10 dark:border-slate-800 dark:bg-slate-900">
                <img
                  src={VILLAGE_DEMO_IMAGE}
                  alt="Un élu municipal présente le site de la commune à des habitants sur un marché de village"
                  className="aspect-[16/10] w-full object-cover"
                  loading="lazy"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-slate-950/10 to-transparent" />

                {/* <figcaption className="absolute bottom-5 left-5 right-5 rounded-[1.6rem] border border-white/15 bg-white/12 p-4 text-white shadow-xl shadow-black/20 backdrop-blur-md">
                  <p className="text-xs font-bold uppercase tracking-[0.22em] text-white/70">
                    Vie locale
                  </p>

                  <p className="mt-1 text-lg font-bold md:text-xl">
                    Une solution pensée pour la commune et ses habitants.
                  </p>

                  <p className="mt-1 text-sm font-medium leading-6 text-white/80">
                    Simple à présenter, simple à consulter, simple à mettre à jour.
                  </p>
                </figcaption> */}
              </figure>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function ContactSection() {
  return (
    <section
      id="contact-demo"
      className="relative overflow-hidden bg-slate-950 px-4 py-14 text-white sm:px-6 lg:px-8 lg:py-20"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-24 top-16 h-80 w-80 rounded-full bg-sky-500/20 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-96 w-96 rounded-full bg-emerald-400/15 blur-3xl" />
      </div>

      <div className="relative mx-auto grid max-w-[1380px] gap-8 lg:grid-cols-[0.95fr_1.05fr] lg:items-center">
        <SectionTitle
          dark
          eyebrow="Contact"
          title="Vous souhaitez voir MonAppliMairie en action ?"
          description="Je peux vous présenter l’application en visio ou par téléphone, vous montrer le site côté habitants et l’espace de gestion côté mairie."
        />

        <Reveal>
          <div className="rounded-[2.2rem] border border-white/10 bg-white/10 p-5 shadow-2xl shadow-black/20 backdrop-blur">
            <div className="grid gap-4 sm:grid-cols-2">
              <a
                href={`tel:${CONTACT_PHONE}`}
                className="group rounded-[1.7rem] bg-white p-5 text-slate-950 shadow-xl shadow-black/10 transition hover:-translate-y-1 hover:bg-slate-100"
              >
                <FaPhoneAlt className="h-7 w-7 text-[#1277B9]" />
                <h3 className="mt-4 text-xl font-bold text-slate-950">
                  Appel téléphonique
                </h3>
                <p className="mt-2 text-sm font-semibold text-slate-600">
                  {CONTACT_PHONE_LABEL}
                </p>
                <span className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-[#1277B9]">
                  Appeler maintenant
                  <FaArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
                </span>
              </a>

              <a
                href={`mailto:${CONTACT_EMAIL}?subject=Demande de démonstration MonAppliMairie`}
                className="group rounded-[1.7rem] bg-white p-5 text-slate-950 shadow-xl shadow-black/10 transition hover:-translate-y-1 hover:bg-slate-100"
              >
                <FaEnvelope className="h-7 w-7 text-emerald-500" />
                <h3 className="mt-4 text-xl font-bold text-slate-950">
                  Demande par email
                </h3>
                <p className="mt-2 break-all text-sm font-semibold text-slate-600">
                  {CONTACT_EMAIL}
                </p>
                <span className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-[#1277B9]">
                  Envoyer un email
                  <FaArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
                </span>
              </a>
            </div>

            <div className="mt-4 rounded-[1.7rem] border border-white/10 bg-slate-950/60 p-5">
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/10 text-sky-300">
                  <FaRegPlayCircle className="h-5 w-5" />
                </div>

                <div>
                  <h3 className="text-lg font-bold text-white">
                    Démonstration personnalisée
                  </h3>
                  <p className="mt-2 text-sm leading-7 text-slate-200">
                    En quelques minutes, vous pouvez voir comment publier une
                    actualité, ajouter un événement, modifier une page et
                    consulter les messages reçus depuis le site.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function AppPresentationPage() {
  useScrollToTop();

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      <ScrollIndicator />

      <HeroSection />

      <main>
        <FullImageSection />
        <ReassuranceSection />
        <FeaturesSection />
        <PreviewSlider />
        <PricingSection />
        <HumanDemoSection />
        <ContactSection />
      </main>
    </div>
  );
}